import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  calculateSaleProductCost,
  createSaleProduct,
} from "../api/saleProductsApi";
import SaleProductComponentRow from "../components/SaleProductComponentRow";
import SaleProductComponentSearch from "../components/SaleProductComponentSearch";
import {
  buildCompositionPayload,
  buildSaleProductPayload,
  canonicalFormSignature,
  componentKey,
  formatDecimalInput,
  normalizeDecimalInput,
  normalizeSectors,
  parsePrice,
  parseQuantity,
  previewSignature,
  SALE_PRODUCT_SECTORS,
} from "../utils/saleProductForm.utils";
import { saleProductReviewReasonLabel } from "../utils/saleProductStatus.utils";

const LIST_PATH = "/stock/base-de-datos/productos-de-venta";
const PREVIEW_DELAY_MS = 450;

const ALL_COMPONENT_KINDS = ["ingredient", "preparedProduct", "recipe"];

function reviewAffectedKinds(reasons = []) {
  const affected = new Set();
  reasons.forEach((reason) => {
    const value = String(reason || "");
    if (
      value === "COMPONENT_INACTIVE" ||
      value === "COMPONENT_DELETED" ||
      value === "ACTIVATION_VALIDATION_FAILED"
    ) {
      ALL_COMPONENT_KINDS.forEach((kind) => affected.add(kind));
    } else if (value.startsWith("INGREDIENT_")) {
      affected.add("ingredient");
    } else if (value.startsWith("PREPARED_")) {
      affected.add("preparedProduct");
    } else if (value.startsWith("RECIPE_") || value === "MISSING_ACTIVE_RECIPE_VERSION") {
      affected.add("recipe");
    }
  });
  return affected;
}

function errorToMessage(error, fallback) {
  if (error?.status === 409 || error?.response?.status === 409) {
    return "Ya existe un producto de venta con ese nombre.";
  }
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.detail ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message) ? message.join(" | ") : message || fallback;
}

function SectionTitle({ children, helper }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 16, fontWeight: 900, color: "primary.900" }}>
        {children}
      </Typography>
      {helper ? (
        <Typography sx={{ mt: 0.2, fontSize: 11.5, fontWeight: 600, color: "neutral.600" }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function SaleProductFormPage({
  initialValues,
  mode = "create",
  reviewMode = false,
  pageTitle = "Agregar producto",
  onSave,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const listState = location.state?.listState;
  const latestPreviewId = useRef(0);
  const submittingRef = useRef(false);
  const [name, setName] = useState(initialValues?.name || "");
  const [sector, setSector] = useState(() =>
    normalizeSectors(initialValues?.sector || ["Cocina"])
  );
  const [price, setPrice] = useState(formatDecimalInput(initialValues?.price ?? ""));
  const [components, setComponents] = useState(() => initialValues?.components || []);
  const [preview, setPreview] = useState(null);
  const [previewStatus, setPreviewStatus] = useState("empty");
  const [previewError, setPreviewError] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [previewedSignature, setPreviewedSignature] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const compositionSignature = useMemo(
    () => previewSignature(components),
    [components]
  );
  const formSignature = useMemo(
    () => canonicalFormSignature({ name, sector, price, components }),
    [components, name, price, sector]
  );
  const initialSignatureRef = useRef(
    canonicalFormSignature({
      name: initialValues?.name || "",
      sector: initialValues?.sector || ["Cocina"],
      price: initialValues?.price ?? "",
      components: initialValues?.components || [],
    })
  );
  const dirty = formSignature !== initialSignatureRef.current;
  const affectedKinds = useMemo(
    () => reviewAffectedKinds(initialValues?.costReviewReasons),
    [initialValues?.costReviewReasons]
  );
  const quantitiesValid =
    components.length > 0 && components.every((component) => parseQuantity(component.quantity) !== null);
  const generalValid =
    name.trim().length > 0 &&
    name.trim().length <= 200 &&
    sector.length > 0 &&
    parsePrice(price) !== null &&
    quantitiesValid;

  useEffect(() => {
    if (!components.length) {
      latestPreviewId.current += 1;
      setPreview(null);
      setPreviewStatus("empty");
      setPreviewedSignature("");
      return undefined;
    }
    if (!quantitiesValid) {
      latestPreviewId.current += 1;
      setPreview(null);
      setPreviewStatus("invalid");
      setPreviewedSignature("");
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++latestPreviewId.current;
    setPreviewStatus("scheduled");
    setPreviewError("");
    const timeout = setTimeout(async () => {
      setPreviewStatus("loading");
      const payload = buildCompositionPayload(components);
      try {
        const result = await calculateSaleProductCost(payload, {
          signal: controller.signal,
        });
        if (controller.signal.aborted || requestId !== latestPreviewId.current) return;
        setPreview(result);
        setPreviewedSignature(compositionSignature);
        setPreviewStatus("success");
      } catch (requestError) {
        if (controller.signal.aborted || requestId !== latestPreviewId.current) return;
        setPreview(null);
        setPreviewedSignature("");
        setPreviewError(
          errorToMessage(requestError, "No se pudo validar la composición.")
        );
        setPreviewStatus("error");
      }
    }, PREVIEW_DELAY_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [components, compositionSignature, previewVersion, quantitiesValid]);

  const previewIsCurrent =
    previewStatus === "success" &&
    previewedSignature === compositionSignature &&
    preview?.canSave === true;
  const canSubmit =
    generalValid &&
    previewIsCurrent &&
    !saving &&
    (mode !== "edit" || reviewMode || dirty);

  const updateComponent = useCallback((target, changes) => {
    setComponents((current) =>
      current.map((component) =>
        componentKey(component) === componentKey(target)
          ? { ...component, ...changes }
          : component
      )
    );
    setErrors((current) => ({ ...current, components: "", lines: {} }));
  }, []);

  function addComponent(option) {
    setComponents((current) =>
      current.some((component) => componentKey(component) === componentKey(option))
        ? current
        : [...current, { ...option, quantity: "" }]
    );
    setErrors((current) => ({ ...current, components: "" }));
  }

  function removeComponent(target) {
    setComponents((current) =>
      current.filter((component) => componentKey(component) !== componentKey(target))
    );
  }

  function validateComponentQuantity(component) {
    const key = componentKey(component);
    setErrors((current) => ({
      ...current,
      lines: {
        ...(current.lines || {}),
        [key]:
          parseQuantity(component.quantity) === null
            ? "Cantidad positiva, máximo 4 decimales."
            : "",
      },
    }));
  }

  function validate() {
    const next = { lines: {} };
    if (!name.trim()) next.name = "Ingresá el nombre del producto.";
    else if (name.trim().length > 200) next.name = "El nombre no puede superar 200 caracteres.";
    if (!sector.length) next.sector = "Seleccioná al menos un sector.";
    if (parsePrice(price) === null) next.price = "Ingresá un precio válido con hasta 2 decimales.";
    if (!components.length) next.components = "Agregá al menos un componente.";
    components.forEach((component) => {
      if (parseQuantity(component.quantity) === null) {
        next.lines[componentKey(component)] = "Cantidad positiva, máximo 4 decimales.";
      }
    });
    setErrors(next);
    return (
      !next.name &&
      !next.sector &&
      !next.price &&
      !next.components &&
      Object.keys(next.lines).length === 0
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (
      submittingRef.current ||
      !validate() ||
      (mode === "edit" && !reviewMode && !dirty)
    ) return;
    submittingRef.current = true;
    setSaving(true);
    setSubmitError("");
    try {
      if (!previewIsCurrent) {
        setPreviewStatus("loading");
        const currentPreview = await calculateSaleProductCost(
          buildCompositionPayload(components)
        );
        if (currentPreview?.canSave !== true) {
          setPreview(currentPreview);
          setPreviewedSignature(compositionSignature);
          setPreviewStatus("success");
          throw new Error("La composición contiene datos que deben revisarse.");
        }
        setPreview(currentPreview);
        setPreviewedSignature(compositionSignature);
        setPreviewStatus("success");
      }
      const payload = buildSaleProductPayload({
        name,
        sector,
        price,
        components,
      });
      if (onSave) await onSave(payload);
      else await createSaleProduct(payload);
      const successMessage =
        mode === "edit"
          ? "El producto se actualizó correctamente."
          : "El producto se creó correctamente.";
      navigate(LIST_PATH, {
        replace: true,
        state: { successMessage, listState },
      });
    } catch (requestError) {
      setSubmitError(
        errorToMessage(requestError, "No se pudo guardar el producto de venta.")
      );
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  const counts = components.reduce(
    (result, component) => ({ ...result, [component.kind]: result[component.kind] + 1 }),
    { ingredient: 0, preparedProduct: 0, recipe: 0 }
  );

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={handleSubmit}
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        "@media (max-height: 700px)": { p: 1.25, gap: 1 },
      }}
    >
      <StockPageHeader
        title={pageTitle}
        onBack={() => navigate(LIST_PATH, { state: { listState } })}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          bgcolor: "transparent",
          borderRadius: 0,
          p: 0,
          boxShadow: "none",
        }}
      >
        <Stack
          spacing={2}
          sx={{
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            p: { xs: 2, md: 3 },
            bgcolor: "neutral.50",
            borderRadius: "22px",
            "@media (max-height: 700px)": { p: 1.5 },
            "& .MuiOutlinedInput-root": { bgcolor: "common.white" },
          }}
        >
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          {initialValues?.costStatus === "REQUIRES_REVIEW" ? (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.7,
                py: 1.2,
                borderRadius: "14px",
                bgcolor: "rgba(195, 60, 60, 0.08)",
                border: "1px solid rgba(195, 60, 60, 0.25)",
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "error.main" }}>
                El costo requiere revisión
              </Typography>
              {(initialValues.costReviewReasons || []).map((reason) => (
                <Typography key={reason} sx={{ fontSize: 12, color: "neutral.700" }}>
                  {saleProductReviewReasonLabel(reason)}
                </Typography>
              ))}
            </Box>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.72fr 1.28fr" },
              gap: { xs: 3, md: 4 },
              alignItems: "stretch",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pr: { xs: 0.5, md: 0.75 },
              scrollbarGutter: "stable",
            }}
          >
            <Stack spacing={2.4}>
              <SectionTitle>Información General</SectionTitle>
              <TextField
                label="Nombre"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setErrors((current) => ({ ...current, name: "" }));
                }}
                onBlur={() =>
                  setErrors((current) => ({
                    ...current,
                    name: name.trim() ? "" : "Ingresá el nombre del producto.",
                  }))
                }
                error={Boolean(errors.name)}
                helperText={errors.name || `${name.length}/200`}
                disabled={saving}
                inputProps={{ maxLength: 200 }}
                size="small"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "white" } }}
              />

              <Box>
                <Typography sx={{ mb: 0.8, fontSize: 12, fontWeight: 800 }}>Sector</Typography>
                <ToggleButtonGroup
                  value={sector}
                  disabled={saving}
                  onChange={(_, value) => {
                    const normalized = normalizeSectors(value);
                    setSector(normalized);
                    setErrors((current) => ({
                      ...current,
                      sector: normalized.length
                        ? ""
                        : "Seleccioná al menos un sector.",
                    }));
                  }}
                  aria-label="Sectores del producto"
                  sx={{
                    gap: 1,
                    "& .MuiToggleButtonGroup-grouped": {
                      minWidth: 108,
                      border: "1px solid rgba(64,64,94,.18) !important",
                      borderRadius: "10px !important",
                      py: 0.8,
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: "none",
                      "&.Mui-selected": {
                        bgcolor: "primary.500",
                        color: "logoblanco",
                        "&:hover": { bgcolor: "primary.600" },
                      },
                    },
                  }}
                >
                  {SALE_PRODUCT_SECTORS.map((value) => (
                    <ToggleButton key={value} value={value}>{value}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {errors.sector ? (
                  <Typography sx={{ mt: 0.5, fontSize: 11, color: "error.main" }}>
                    {errors.sector}
                  </Typography>
                ) : null}
              </Box>

              <TextField
                label="Precio"
                value={price}
                onChange={(event) => {
                  setPrice(normalizeDecimalInput(event.target.value));
                  setErrors((current) => ({ ...current, price: "" }));
                }}
                onBlur={() => {
                  setPrice((value) => formatDecimalInput(value));
                  setErrors((current) => ({
                    ...current,
                    price:
                      parsePrice(price) === null
                        ? "Ingresá un precio válido con hasta 2 decimales."
                        : "",
                  }));
                }}
                error={Boolean(errors.price)}
                helperText={errors.price || "Precio de venta"}
                disabled={saving}
                inputProps={{ inputMode: "decimal", maxLength: 14 }}
                size="small"
                sx={{ maxWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "white" } }}
              />
            </Stack>

            <Stack
              spacing={1.2}
              sx={{
                minWidth: 0,
                minHeight: 0,
                height: "100%",
                overflow: "hidden",
                pl: { xs: 0, md: 4 },
                borderLeft: { xs: 0, md: "2px solid rgba(2,8,23,.35)" },
              }}
            >
              <SectionTitle helper="Buscá y agregá insumos, elaborados o recetas">
                Contenido
              </SectionTitle>
              <SaleProductComponentSearch
                selected={components}
                disabled={saving}
                onSelect={addComponent}
              />
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "neutral.600" }}>
                {counts.ingredient} insumos · {counts.preparedProduct} elaborados · {counts.recipe} recetas
              </Typography>
              {errors.components ? <Alert severity="warning">{errors.components}</Alert> : null}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  pr: 0.7,
                  borderRadius: "10px",
                  scrollbarGutter: "stable",
                }}
              >
                {components.length ? (
                  components.map((component) => (
                    <SaleProductComponentRow
                      key={componentKey(component)}
                      component={component}
                      reviewAffected={reviewMode && affectedKinds.has(component.kind)}
                      error={errors.lines?.[componentKey(component)]}
                      disabled={saving}
                      onChange={(changes) => updateComponent(component, changes)}
                      onBlur={() => validateComponentQuantity(component)}
                      onRemove={() => removeComponent(component)}
                    />
                  ))
                ) : (
                  <Typography sx={{ py: 2, fontSize: 12, color: "neutral.600" }}>
                    Todavía no agregaste componentes.
                  </Typography>
                )}
              </Box>

              {previewStatus === "scheduled" || previewStatus === "loading" ? (
                <Stack direction="row" spacing={0.8} sx={{ alignItems: "center" }}>
                  <CircularProgress size={14} thickness={5} />
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "neutral.600" }}>
                    Validando composición…
                  </Typography>
                </Stack>
              ) : null}
              {previewStatus === "error" ? (
                <Alert
                  severity="error"
                  action={(
                    <Button
                      type="button"
                      size="small"
                      onClick={() => setPreviewVersion((value) => value + 1)}
                      sx={{ fontWeight: 900, textTransform: "none" }}
                    >
                      Reintentar
                    </Button>
                  )}
                >
                  {previewError}
                </Alert>
              ) : null}
              {previewStatus === "success" && preview?.canSave === false ? (
                <Alert severity="warning">
                  {Array.from(
                    new Set(
                      (preview?.issues || [])
                        .map((issue) => issue?.message || issue?.code)
                        .filter(Boolean)
                    )
                  ).join(" · ") || "La composición contiene datos que deben revisarse."}
                </Alert>
              ) : null}
            </Stack>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          <Stack
            direction="row"
            spacing={1.2}
            sx={{
              flexShrink: 0,
              position: "relative",
              zIndex: 2,
              justifyContent: "flex-end",
              bgcolor: "neutral.50",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              disabled={saving}
              onClick={() => navigate(LIST_PATH, { state: { listState } })}
              sx={{ minWidth: 110, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              startIcon={<SaveIcon />}
              sx={{ minWidth: 150, borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
            >
              {saving ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Guardar"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
